import { FC, useState, useEffect } from 'react';
import {
  Button,
  Modal,
  SelectMenu,
  Spinner,
  TextArea,
  TextField,
  Typography,
  Switch,
} from '@scale/scaleui';
import { TeamUuid, Priority, isValidPriority, LabelUuid } from '@scale/shared/reportIssue/types';
import { useReportIssue } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
import { successToast } from 'react-scale/corp/views/CorpProjectGroupManager/components/ReportIssue/ReportIssueSuccessToast';
import { doOnFsReady, getCurrentSessionURL } from 'react-scale/lib/fullstory';
import { Button as RadixButton, Icon, Text } from '@scale/scaleui-radix';
import { faFlag } from '@fortawesome/pro-regular-svg-icons';

const MAX_TITLE_LENGTH = 60;

// Team options for PagerDuty incidents
const TEAM_TO_PD_SERVICE = [
  { id: 'Operator app / OpsHub', name: 'Operator app / OpsHub', serviceId: 'PCHB4I0' },
  { id: 'Customer App / Evals', name: 'Customer App / Evals', serviceId: 'PCJAQRD' },
  { id: 'Allocation', name: 'Allocation', serviceId: 'P1J13XB' },
  { id: 'Chat Task', name: 'Chat Task', serviceId: 'PQYU3UY' },
  { id: 'Pay', name: 'Pay', serviceId: 'PVHDUQW' },
  { id: 'Growth', name: 'Growth', serviceId: 'PDLWUNV' },
  { id: 'Connectivity', name: 'Connectivity', serviceId: 'P7DKGGI' },
  { id: 'Quality', name: 'Quality', serviceId: 'PJATR4K' },
  { id: 'Outlier', name: 'Outlier', serviceId: 'PDUROJP' },
  { id: 'Violations', name: 'Violations', serviceId: 'PFSUY2C' },
  { id: 'Product Infra', name: 'Product Infra', serviceId: 'PDJJPRQ' },
  { id: 'PagerDuty Manager', name: 'PagerDuty Manager', serviceId: 'PE50O2W' },
  // Add other teams as needed
];

// Only expose a few priorities to user.
const PRIORITIES = [Priority.Urgent, Priority.High, Priority.Medium, Priority.Low];
const PRIORITY_OPTIONS = (PRIORITIES.filter(value => typeof value === 'number') as Priority[]).map(
  (priority: Priority) => ({
    id: priority.toString(),
    name: priorityToDescription(priority),
  }),
);
function priorityToDescription(priority?: Priority): string {
  switch (priority) {
    case Priority.Urgent:
      return 'Urgent';
    case Priority.High:
      return 'High';
    case Priority.Medium:
      return 'Medium';
    case Priority.Low:
      return 'Low';
    default:
      return 'Other';
  }
}

export const ReportIssueButton: FC = () => {
  const slackTeamName = '@gen-ai-ops-app-eng';

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const { mutateAsync: reportIssue, isLoading } = useReportIssue();

  // Issue input.
  const [issuePriority, setIssuePriority] = useState<Priority>(Priority.NoPriority);
  const [issueTitle, setIssueTitle] = useState<string>('');
  const [issueDescription, setIssueDescription] = useState<string>('');
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [isPagerPolicyChecked, setIsPagerPolicyChecked] = useState<boolean>(false);
  const [fullstoryUrl, setFullstoryUrl] = useState<string>('');

  const resetIssueInput = () => {
    setIssuePriority(Priority.NoPriority);
    setIssueTitle('');
    setIssueDescription('');
    setSelectedTeam('');
    setIsPagerPolicyChecked(false);
  };

  useEffect(() => {
    doOnFsReady(() => {
      const url = getCurrentSessionURL(true);
      setFullstoryUrl(url);
    });
  }, []);

  // Create the Linear issue and optionally page.
  const handleReportIssue = async () => {
    if (!canSubmit) {
      return;
    }
    const selectedTeamOption = TEAM_TO_PD_SERVICE.find(team => team.id === selectedTeam);
    const serviceId = selectedTeamOption ? selectedTeamOption.serviceId : undefined;
    const res = await reportIssue({
      teamUuid: TeamUuid.OPAPI,
      priority: issuePriority,
      labelUuids: [LabelUuid.ReqOnCall], // Include ReqOnCall label.
      title: issueTitle,
      description: `
      \n## Description 
      \n\n${issueDescription}
      \n\n## Additional context
      \n\nURL: ${window.location.href}
      \n\nFullstory Session URL: ${fullstoryUrl}
      `,
      ...(serviceId && { serviceId }),
    });

    setIsOpen(false);
    resetIssueInput();

    if (res) {
      successToast(res.issue.title, res.issue.url, slackTeamName, selectedTeam);
    }
  };

  const canSubmit =
    !isLoading &&
    isValidPriority(issuePriority) &&
    issueTitle &&
    // If pager is checked, require team selection
    (!isPagerPolicyChecked || (isPagerPolicyChecked && selectedTeam));

  return (
    <>
      <Modal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title={'Report product issue'}
        className="scale-tailwind"
        innerClassName="w-2/5 p-2"
      >
        <div className="flex flex-col items-start gap-6">
          <TextField
            className="w-full"
            label="Describe the issue in a few words"
            value={issueTitle}
            required={true}
            onChange={e => {
              const value = e.target.value;
              if (value.length <= MAX_TITLE_LENGTH) {
                setIssueTitle(value);
              }
            }}
          />
          <SelectMenu
            className="w-full"
            label="Priority"
            required={true}
            options={PRIORITY_OPTIONS}
            selectedOptionId={issuePriority.toString()}
            setSelected={priority => {
              setIssuePriority(Number(priority));
            }}
          />
          <TextArea
            className="w-full"
            label="More details about the issue"
            helperText="Please be as detailed as possible. e.g. What happens when the issue occurs? What do you expect to happen? Is this blocking a delivery? No need to provide the URL or your name, we'll gather that info automatically!"
            value={issueDescription}
            onChange={e => setIssueDescription(e.target.value)}
          />
          <div>
            <div className="mb-2">
              <Text size="3">Optionally page engineering?</Text>
              <div className="py-2 flex items-center gap-2">
                <Switch checked={isPagerPolicyChecked} onCheckedChange={setIsPagerPolicyChecked} />
                <Text size="2" className="text-neutral-600">
                  This is an emergency (P0). I understand switching this on will page an engineer,
                  potentially waking them up at night.
                </Text>
              </div>
              {isPagerPolicyChecked && (
                <>
                  <div className="text-xs text-neutral-600 mt-2">
                    Select the team you think is most relevant to call an engineer on that team (day
                    or night). See go/routing.
                  </div>
                  <div className="w-[250px] mt-2">
                    <SelectMenu
                      className="w-full"
                      required={false}
                      options={TEAM_TO_PD_SERVICE}
                      selectedOptionId={selectedTeam}
                      setSelected={team => {
                        setSelectedTeam(team);
                      }}
                      placeholder="Select team"
                    />
                  </div>
                </>
              )}
            </div>
          </div>
          <Typography.Caption>
            <strong>{slackTeamName}</strong> will review the issue and reach out if we need more
            information.
          </Typography.Caption>
          {isLoading ? (
            <Spinner size="xs" />
          ) : (
            <Button
              className="w-fit"
              variant="primary"
              size="lg"
              onClick={handleReportIssue}
              disabled={!canSubmit}
            >
              Submit
            </Button>
          )}
        </div>
      </Modal>

      <RadixButton
        size="2"
        variant="outline"
        color="neutral"
        className="gap-2 text-[#6E39F2] text-[16px] font-medium font-inter leading-[24px] tracking-[0px]"
        onClick={() => setIsOpen(true)}
      >
        <Icon label="Report Issue" icon={faFlag} size="1" />
        <Text size="2" color="neutral">
          Report Issue
        </Text>
      </RadixButton>
    </>
  );
};
